'use client';
import { BaseTranslations } from '../../baseTranslations';
import { CmsProduct } from '../../types/cmsProduct';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../utils/analytics/sendAnalytics';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getApplyButtonLabel } from '../../utils/helpers/router';
import { sessionAttributionTracker } from '../../utils/sessionAttributionTracking/sessionAttributionTracker';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import Button from '../Button';
import OpenInNew from '../icons/OpenInNew';

type ProductCardButtonProps = {
  product: CmsProduct;
  pushDataLayer: PushDataLayerFn;
  /**
   * Replaces the apply click handler. The analytics it emits describe an apply
   * — `product_click_apply` plus an attribution record that overwrites the
   * session's apply source — so a CTA doing something else must not fire them
   * (CMS-1954).
   */
  onClick?: () => void;
};

const ProductCardButton: React.FC<ProductCardButtonProps> = ({
  product,
  pushDataLayer,
  onClick,
}) => {
  const context = useCmsClientContext();
  const { provider, call_to_action } = product;
  const staticText = getClientTranslations(BaseTranslations, context);

  const handleApplyClick = () => {
    sessionAttributionTracker.recordApplyClick();
    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: 'Product Card/CTA_Apply Now',
      componentType: ComponentTypeEnum.PRODUCT_PAGE,
      event: 'product_click_apply',
      index: '',
      product,
      pushDataLayer,
    });
  };
  return (
    <div
      id="applyNow"
      className="sticky bottom-0 left-0 z-20 w-full bg-white lg:relative lg:!m-0"
    >
      <div className="flex w-full flex-col justify-center px-6 py-4 lg:p-0 lg:pt-4">
        <Button
          styleType="cta"
          endIcon={<OpenInNew />}
          onClick={onClick ?? handleApplyClick}
          className="w-full bg-brand-cta"
        >
          {getApplyButtonLabel(
            tr('apply_now', staticText),
            undefined,
            call_to_action,
          )}
        </Button>
        {provider && (
          <p className="mt-2 text-center font-normal text-gray-700 text-base leading-normal">
            {tr('on_provider_website', staticText, {
              provider: provider.provider_name,
            })}
          </p>
        )}
      </div>
    </div>
  );
};

export default ProductCardButton;
